/**
 * EmptyState — what a list says when it has nothing to show.
 *
 * Distinct from `MockedOverlay` ("this data is fake") and from an error: the
 * request succeeded and the answer is genuinely nothing. The `hint` is where
 * the way out goes, because "no results" without "clear the search" is a dead
 * end the reader has to reason their way out of.
 */
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

export interface EmptyStateProps {
  icon?: LucideIcon;
  /** One line, stating what is not there. */
  title: string;
  /** What to do about it. Omit when there is nothing the reader can do. */
  hint?: ReactNode;
}

export function EmptyState({ icon: Icon, title, hint }: EmptyStateProps) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        padding: '48px 24px',
        textAlign: 'center',
        color: 'var(--nb-subtle)',
      }}
    >
      {Icon && <Icon size={26} aria-hidden="true" style={{ opacity: 0.55 }} />}
      <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: 'var(--nb-ink)' }}>{title}</p>
      {hint && <p style={{ margin: 0, fontSize: 12.5, maxWidth: 320 }}>{hint}</p>}
    </div>
  );
}
